import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Title,
  DropdownSection,
  DropdownContainer,
  PrinterGrid,
  PrinterCard,
  PrinterImageContainer,
  PrinterImage,
  Divider,
  FeaturesList,
  FullSpecsContainer,
  SpecsLinkButton,
  PdfLabel
} from './styles';
import { useTranslationHelper } from 'src/contexts/dependencyManager/utils';
import { IconDownload, Select } from '@veneer/core';
import { PrinterItem } from 'src/utils/Products/productFilter';
import { PrinterSpecification } from '../PrinterSpecification';
import { DropdownOption } from 'src/types/specifications';
import useIsMobile from 'src/hooks/useIsMobile';

interface PrinterComparisonProps {
  printerDetails?: PrinterItem[];
}

const PrinterComparison: React.FC<PrinterComparisonProps> = ({
  printerDetails
}) => {
  const getText = useTranslationHelper();
  const isMobile = useIsMobile(768);

  // Create options from printer data
  const allOptions = useMemo(() => {
    if (!printerDetails || printerDetails.length === 0) return [];
    return printerDetails.map((printer, index) => ({
      label: printer.printerName,
      value: index + 1
    }));
  }, [printerDetails]);

  // Selected printer indices - 2 for mobile, 3 for web
  const [selectedPrinters, setSelectedPrinters] = useState<number[]>(
    isMobile ? [0, 1] : [0, 1, 2]
  );

  // Update selectedPrinters when screen size changes
  useEffect(() => {
    setSelectedPrinters((prevSelections) => {
      if (isMobile) {
        // Mobile: keep only first 2 selections
        return prevSelections.slice(0, 2);
      } else {
        // Web: ensure we have 3 selections
        if (prevSelections.length < 3) {
          const availableIndices = allOptions
            .map((_, index) => index)
            .filter((index) => !prevSelections.includes(index));
          const thirdSelection = availableIndices[0] ?? 2;
          return [...prevSelections, thirdSelection];
        }
        return prevSelections;
      }
    });
  }, [isMobile, allOptions]);

  // Get options for each dropdown
  const getDropdownOptions = (dropdownIndex: number) => {
    if (!allOptions.length) return [];

    const selectedPrinterIndex = selectedPrinters[dropdownIndex];
    const currentOption = allOptions[selectedPrinterIndex];
    const maxSelections = isMobile ? 2 : 3;

    if (allOptions.length === maxSelections) {
      // Only show current selection if we have exactly as many printers as dropdowns
      return [currentOption];
    }

    // Show current selection + unselected options
    const unselectedOptions = allOptions.filter(
      (_, index) => !selectedPrinters.includes(index)
    );

    return [currentOption, ...unselectedOptions];
  };

  // Handle dropdown selection
  const handleSelection = (
    dropdownIndex: number,
    selectedOption: DropdownOption
  ) => {
    const newPrinterIndex = selectedOption.value - 1;

    setSelectedPrinters((prev) => {
      const newSelections = [...prev];
      newSelections[dropdownIndex] = newPrinterIndex;
      return newSelections;
    });
  };

  // Get current printer list for display
  const currentPrinters = useMemo(() => {
    if (!printerDetails) return [];

    const maxDisplayCount = isMobile ? 2 : 3;
    const printers = selectedPrinters
      .map((index) => printerDetails[index])
      .filter(Boolean)
      .slice(0, maxDisplayCount);

    // Parse specs and remove keys that are false across all printers
    const parsedPrinters = printers.map((printer) => ({
      ...printer,
      specs: JSON.parse(printer.specificationList || '{}')
    }));

    // If any printer has aiEnhanced: true, ensure all printers have this key
    const hasAiEnhanced = parsedPrinters.some(
      (p) => p.specs.aiEnhanced === true
    );
    if (hasAiEnhanced) {
      parsedPrinters.forEach((printer) => {
        if (!('aiEnhanced' in printer.specs)) {
          printer.specs.aiEnhanced = false;
        }
      });
    }

    // Find and remove false-only keys
    const sampleSpecs = parsedPrinters[0]?.specs || {};
    const filteredPrinters = parsedPrinters.map((printer) => {
      const cleanSpecs = { ...printer.specs };

      Object.keys(sampleSpecs).forEach((key) => {
        if (parsedPrinters.every((p) => p.specs[key] === false)) {
          delete cleanSpecs[key];
        }
      });

      return { ...printer, specificationList: JSON.stringify(cleanSpecs) };
    });

    return filteredPrinters;
  }, [printerDetails, selectedPrinters, isMobile]);

  return (
    <Container>
      <Title>
        {getText(
          'printerComparison.title',
          'Select the models you want to compare'
        )}
      </Title>

      <DropdownSection>
        <DropdownContainer>
          <Select
            options={getDropdownOptions(0)}
            id="select-dropdown1"
            value={[selectedPrinters[0] + 1]}
            clearIcon={false}
            onChange={(option) => handleSelection(0, option)}
          />
        </DropdownContainer>
        <DropdownContainer>
          <Select
            options={getDropdownOptions(1)}
            id="select-dropdown2"
            value={[selectedPrinters[1] + 1]}
            clearIcon={false}
            onChange={(option) => handleSelection(1, option)}
          />
        </DropdownContainer>
        {!isMobile && selectedPrinters[2] !== undefined && (
          <DropdownContainer>
            <Select
              options={getDropdownOptions(2)}
              id="select-dropdown3"
              clearIcon={false}
              value={[selectedPrinters[2] + 1]}
              onChange={(option) => handleSelection(2, option)}
            />
          </DropdownContainer>
        )}
      </DropdownSection>

      <PrinterGrid>
        {currentPrinters.map((printer: PrinterItem) => {
          return (
            <PrinterCard key={printer.productSKU}>
              <PrinterImageContainer>
                <PrinterImage
                  src={printer.thumbnailImage}
                  alt={printer.printerName}
                />
              </PrinterImageContainer>

              <FullSpecsContainer>
                <SpecsLinkButton
                  onClick={(e) => {
                    window.open(printer.fullspecification, '_blank');
                    e.preventDefault();
                  }}
                >
                  <IconDownload size={20} />
                  <span>Full specs</span>
                </SpecsLinkButton>
                <PdfLabel>(PDF)</PdfLabel>
              </FullSpecsContainer>

              <Divider />
              <FeaturesList>
                <PrinterSpecification printerItem={printer} />
              </FeaturesList>
            </PrinterCard>
          );
        })}
      </PrinterGrid>
    </Container>
  );
};

export default PrinterComparison;
